import { TooltipComponent } from '@components/core/tooltip.component';
import { FormLabel } from '@components/shared/form-label.component';
import { Badge } from '@components/ui/badge';
import { Textarea } from '@components/ui/textarea';
import { cn } from '@lib/utils';
import { shouldIgnoreSurveyKeyboardShortcut } from '@utils/keyboard-shortcuts.util';
import { useEffect, useRef, useState } from 'react';
import { AnswerResponseProgress, getAnswerDisplayText } from '../../../../types/answer.types';
import { NavigationButtons, ShortcutBadge } from './assessment-question-shared.component';

const HIGHLIGHT_DURATION_MS = 300;

type AssessmentProgressQuestionProps = {
  afterDetailsSlot?: React.ReactNode;
  contextSlot?: React.ReactNode;
  description?: string;
  details: string;
  detailsPlaceholder?: string;
  detailsTooltip?: React.ReactNode;
  disableDetails?: boolean;
  disabled?: boolean;
  footerNote?: string;
  isNextLoading?: boolean;
  isKeyboardNavigationDisabled?: boolean;
  isPreviousLoading?: boolean;
  onDetailsBlur: () => Promise<void> | void;
  onDetailsChange: (value: string) => void;
  onNext: () => Promise<void> | void;
  onOptionChange: (option: string) => void;
  onPrevious: () => Promise<void> | void;
  optionLabels?: Partial<Record<string, string>>;
  optionValues: readonly string[];
  previousQuestion?: boolean;
  prompt: string;
  questionIdentifier?: string;
  questionNumber: number;
  selectedOption?: string | null;
};

export const AssessmentProgressQuestion: React.FC<AssessmentProgressQuestionProps> = (props) => {
  const {
    contextSlot,
    description,
    details,
    detailsPlaceholder,
    detailsTooltip,
    disableDetails,
    disabled,
    footerNote = 'Please select the answer that is most applicable. If "No" or "Not Applicable" please provide more information in the text below.',
    afterDetailsSlot,
    isNextLoading,
    isKeyboardNavigationDisabled,
    isPreviousLoading,
    onDetailsBlur,
    onDetailsChange,
    onNext,
    onOptionChange,
    onPrevious,
    optionLabels,
    optionValues,
    previousQuestion,
    prompt,
    questionIdentifier,
    questionNumber,
    selectedOption,
  } = props;

  const [highlightedOption, setHighlightedOption] = useState<string | null>(null);
  const highlightTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (highlightTimerRef.current) {
        clearTimeout(highlightTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (isKeyboardNavigationDisabled || event.repeat || shouldIgnoreSurveyKeyboardShortcut(event)) return;

      if (event.key === 'ArrowRight') {
        event.preventDefault();
        void onNext();
        return;
      }

      if (event.key === 'ArrowLeft' && previousQuestion) {
        event.preventDefault();
        void onPrevious();
        return;
      }

      if (event.ctrlKey || event.metaKey || event.altKey) return;

      const keyNumber = Number.parseInt(event.key, 10);

      if (keyNumber >= 1 && keyNumber <= optionValues.length) {
        const option = optionValues[keyNumber - 1];

        event.preventDefault();
        if (highlightTimerRef.current) {
          clearTimeout(highlightTimerRef.current);
        }
        setHighlightedOption(option);
        highlightTimerRef.current = setTimeout(() => {
          setHighlightedOption(null);
        }, HIGHLIGHT_DURATION_MS);
        onOptionChange(option);
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isKeyboardNavigationDisabled, onNext, onOptionChange, onPrevious, optionValues, previousQuestion]);

  return (
    <div className="@container/question-content mx-auto w-full max-w-[800px] px-3 pb-6 pt-5">
      <div className="font-medium text-[#3d2eff]">Question {questionNumber}</div>
      {questionIdentifier ? (
        <div className="pt-3">
          <Badge
            size="sm"
            variant="outline"
          >
            {questionIdentifier}
          </Badge>
        </div>
      ) : null}
      <div className="pt-3 text-stone-900/70">
        {prompt}
        <span className="text-indigo-600">*</span>
      </div>

      {description && <div className="pt-3 text-xs text-stone-900/70">{description}</div>}
      {contextSlot}

      <div className="w-full pt-4">
        <div className="flex flex-col gap-2 @min-[36rem]/question-content:flex-row @min-[36rem]/question-content:flex-wrap @min-[36rem]/question-content:items-center @min-[36rem]/question-content:justify-between">
          {optionValues.map((option, index) => (
            <div
              className="flex w-full items-center @min-[36rem]/question-content:w-auto"
              key={option}
            >
              <div
                className={cn(
                  'flex items-center rounded-lg p-3 transition-colors duration-200 hover:bg-[#3D2EFF]/10',
                  (highlightedOption === option || selectedOption === option) && 'bg-[#3D2EFF]/10',
                  disabled ? 'cursor-not-allowed' : 'cursor-pointer',
                )}
                onClick={(event) => {
                  event.stopPropagation();
                  if (!disabled) {
                    onOptionChange(option);
                  }
                }}
              >
                <input
                  checked={selectedOption === option}
                  className="h-4 w-4 border-gray-300 text-[#3d2eff] focus:ring-[#3d2eff]"
                  disabled={disabled}
                  id={option}
                  name="answer"
                  onChange={(event) => {
                    event.stopPropagation();
                    if (!disabled) {
                      onOptionChange(option);
                    }
                  }}
                  type="radio"
                  value={option}
                />
                <label
                  className={cn(
                    'ml-2 flex items-center gap-2 text-sm font-medium text-gray-700 @min-[36rem]/question-content:whitespace-nowrap',
                    disabled ? 'cursor-not-allowed' : 'cursor-pointer',
                  )}
                  htmlFor={option}
                >
                  {optionLabels?.[option] ?? getAnswerDisplayText(option as AnswerResponseProgress)}
                  <ShortcutBadge
                    className="text-gray-500 underline"
                    shortcut={String(index + 1)}
                  />
                </label>
              </div>
            </div>
          ))}
        </div>

        <div className="pt-6 text-xs leading-tight text-stone-900/70">{footerNote}</div>
      </div>

      <div className="mt-6 flex w-full flex-col items-start gap-1.5">
        <div className="flex items-center gap-2">
          <FormLabel
            htmlFor="details"
            label="Additional Details"
          />
          {detailsTooltip && <TooltipComponent side="bottom">{detailsTooltip}</TooltipComponent>}
        </div>
        <Textarea
          className="w-full"
          disabled={disableDetails ?? disabled}
          id="details"
          name="details"
          onBlur={() => void onDetailsBlur()}
          onChange={(event) => onDetailsChange(event.target.value)}
          placeholder={detailsPlaceholder}
          value={details}
        />
      </div>

      {afterDetailsSlot}

      <div className="pr-2.5 pt-2.5">
        <NavigationButtons
          isNextLoading={isNextLoading}
          isPreviousLoading={isPreviousLoading}
          onNext={() => void onNext()}
          onPrevious={() => void onPrevious()}
          previousQuestion={previousQuestion}
        />
      </div>
    </div>
  );
};
